import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { FINANCING_KINDS, FINANCING_KIND_LABELS, FINANCING_OBJECT_KINDS, FINANCING_OBJECT_LABELS, formatCents, type CreateProjectInput, type FinancingKind, type FinancingProject, type ProjectAssessment } from '@kontor/shared';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../api';
import { parseCents } from '../../money';
import { useSession } from '../../session';
import { AffordabilityCard } from './AffordabilityCard';
import '../fleet/fleet.css';
import './financing.css';

const centsText = (c: number | null | undefined) => (c ? (c / 100).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '');

/**
 * Vorhaben planen oder ändern (KON-256). Die Angaben entsprechen denen eines Vertrags, dazu kommen Verwendungszweck und Wert des Gegenstands für die Mappe. Während der Eingabe rechnet
 * Wirkia die Monatsrate und prüft ihre Tragfähigkeit gegen die Kostenstruktur. Gespeichert wird nur auf Knopfdruck, die Vorschau legt nichts an.
 */
export function ProjectForm({ project, onSubmit, onCancel, pending, error }: { project?: FinancingProject; onSubmit: (body: CreateProjectInput) => void; onCancel: () => void; pending: boolean; error: string | null }) {
  const { hasModule, can } = useSession();
  const p = project;
  const [kind, setKind] = useState<FinancingKind>(p?.kind ?? 'lease');
  const [title, setTitle] = useState(p?.title ?? '');
  const [lender, setLender] = useState(p?.lenderName ?? '');
  const [objectKind, setObjectKind] = useState(p?.objectKind ?? 'vehicle');
  const [vehicleId, setVehicleId] = useState(p?.vehicleId ?? '');
  const [objectDescription, setObjectDescription] = useState(p?.objectDescription ?? '');
  const [objectValue, setObjectValue] = useState(centsText(p?.objectValueCents));
  const [plannedStart, setPlannedStart] = useState(p?.plannedStart ?? '');
  const [term, setTerm] = useState(String(p?.termMonths ?? 48));
  const [principal, setPrincipal] = useState(centsText(p?.principalCents));
  const [down, setDown] = useState(centsText(p?.downPaymentCents));
  const [rate, setRate] = useState(p?.annualRateBp != null ? (p.annualRateBp / 100).toLocaleString('de-DE') : '');
  const [installment, setInstallment] = useState(p && (p.kind === 'lease' || p.annualRateBp === null) ? centsText(p.installmentCents) : '');
  const [balloon, setBalloon] = useState(centsText(p?.balloonCents));
  const [fees, setFees] = useState(centsText(p?.feesCents));
  const [taxRate, setTaxRate] = useState<0 | 7 | 19>(p?.taxRate ?? 19);
  const [mileage, setMileage] = useState(p?.mileagePerYearKm != null ? String(p.mileagePerYearKm) : '');
  const [purpose, setPurpose] = useState(p?.purpose ?? '');
  const [note, setNote] = useState(p?.note ?? '');
  const [local, setLocal] = useState<string | null>(null);
  const isLease = kind === 'lease';
  const termMonths = Number(term);

  const vehicles = useQuery({
    queryKey: ['fleet-vehicles-picker'],
    queryFn: async () => (await api<{ data: { id: string; plate: string; name: string | null }[] }>('GET', '/fleet/vehicles?perPage=100')).data,
    enabled: hasModule('fleet') && can('fleet.read'),
  });

  const parsed = {
    principal: principal.trim() ? parseCents(principal) : null,
    down: down.trim() ? parseCents(down) : 0,
    rate: rate.trim() ? Math.round(Number(rate.replace(/\./g, '').replace(',', '.')) * 100) : null,
    installment: installment.trim() ? parseCents(installment) : null,
    balloon: balloon.trim() ? parseCents(balloon) : 0,
    fees: fees.trim() ? parseCents(fees) : 0,
    value: objectValue.trim() ? parseCents(objectValue) : null,
  };
  const rateOk = parsed.rate === null || Number.isFinite(parsed.rate);

  const body = useMemo((): CreateProjectInput | null => {
    if (!Number.isInteger(termMonths) || termMonths < 1 || termMonths > 360 || !rateOk || title.trim().length < 2) return null;
    if ([parsed.down, parsed.balloon, parsed.fees].some((v) => v === null)) return null;
    if (isLease ? !parsed.installment : !parsed.principal || (parsed.rate === null && !parsed.installment)) return null;
    return {
      kind,
      title: title.trim(),
      lenderName: lender.trim() || null,
      objectKind,
      vehicleId: objectKind === 'vehicle' && vehicleId ? vehicleId : null,
      objectDescription: objectDescription.trim() || null,
      objectValueCents: parsed.value,
      plannedStart: plannedStart || null,
      termMonths,
      principalCents: isLease ? null : parsed.principal,
      downPaymentCents: parsed.down ?? 0,
      annualRateBp: isLease ? null : parsed.rate,
      installmentCents: parsed.installment,
      balloonCents: parsed.balloon ?? 0,
      feesCents: parsed.fees ?? 0,
      taxRate: isLease ? taxRate : 0,
      mileagePerYearKm: isLease && mileage.trim() ? Number(mileage.replace(/\./g, '')) : null,
      purpose: purpose.trim() || null,
      note: note.trim() || null,
    };
  }, [kind, title, lender, objectKind, vehicleId, objectDescription, plannedStart, termMonths, taxRate, mileage, purpose, note, isLease, rateOk, parsed.principal, parsed.down, parsed.rate, parsed.installment, parsed.balloon, parsed.fees, parsed.value]);

  // Vorschau erst, wenn die Eingabe kurz ruht
  const [settled, setSettled] = useState<CreateProjectInput | null>(null);
  useEffect(() => {
    const t = setTimeout(() => setSettled(body), 500);
    return () => clearTimeout(t);
  }, [body]);
  const preview = useQuery({
    queryKey: ['financing-project-preview', settled],
    queryFn: async () => (await api<{ data: ProjectAssessment & { monthlyCents: number | null } }>('POST', '/financing-projects/preview', settled)).data,
    enabled: settled !== null,
    staleTime: 30_000,
  });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    setLocal(null);
    if (!rateOk) return setLocal('Bitte geben Sie den Zinssatz als Zahl an, zum Beispiel 4,5.');
    if (!body) return setLocal('Bitte füllen Sie die Pflichtangaben aus: Bezeichnung, Laufzeit und bei Leasing die Rate, sonst Betrag und Zinssatz oder Rate.');
    onSubmit(body);
  };

  return (
    <form className="card fleet-form" onSubmit={submit} aria-label={p ? 'Vorhaben bearbeiten' : 'Neues Vorhaben'}>
      <h2>{p ? `Vorhaben ${p.number} bearbeiten` : 'Neues Vorhaben'}</h2>
      {(error || local) && <div className="alert alert--error" role="alert">{local ?? error}</div>}
      <div className="form-grid">
        <label className="field">
          <span>Art</span>
          <select className="input" value={kind} onChange={(e) => setKind(e.target.value as FinancingKind)}>
            {FINANCING_KINDS.map((k) => <option key={k} value={k}>{FINANCING_KIND_LABELS[k]}</option>)}
          </select>
        </label>
        <label className="field"><span>Bezeichnung</span><input className="input" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={150} required placeholder="zum Beispiel Zweite Limousine" /></label>
        <label className="field"><span>Beantragt bei (Bank, Leasinggesellschaft)</span><input className="input" value={lender} onChange={(e) => setLender(e.target.value)} maxLength={150} /></label>
        <label className="field">
          <span>Gegenstand</span>
          <select className="input" value={objectKind} onChange={(e) => setObjectKind(e.target.value as typeof objectKind)}>
            {FINANCING_OBJECT_KINDS.map((k) => <option key={k} value={k}>{FINANCING_OBJECT_LABELS[k]}</option>)}
          </select>
        </label>
        {objectKind === 'vehicle' && (vehicles.data?.length ?? 0) > 0 && (
          <label className="field">
            <span>Fahrzeug der Flotte (falls es schon erfasst ist)</span>
            <select className="input" value={vehicleId} onChange={(e) => setVehicleId(e.target.value)}>
              <option value="">Nicht zugeordnet</option>
              {vehicles.data!.map((v) => <option key={v.id} value={v.id}>{v.plate}{v.name ? ` · ${v.name}` : ''}</option>)}
            </select>
          </label>
        )}
        <label className="field"><span>Beschreibung des Gegenstands</span><input className="input" value={objectDescription} onChange={(e) => setObjectDescription(e.target.value)} maxLength={200} placeholder="Marke, Modell, Ausstattung" /></label>
        <label className="field"><span>Wert des Gegenstands netto (€)</span><input className="input" inputMode="decimal" value={objectValue} onChange={(e) => setObjectValue(e.target.value)} /></label>
        <label className="field"><span>Geplanter Beginn</span><input className="input" type="date" value={plannedStart} onChange={(e) => setPlannedStart(e.target.value)} /></label>
        <label className="field"><span>Laufzeit in Monaten</span><input className="input" inputMode="numeric" value={term} onChange={(e) => setTerm(e.target.value.replace(/\D/g, ''))} required /></label>
        {!isLease && <label className="field"><span>{kind === 'loan' ? 'Darlehensbetrag (€)' : 'Finanzierter Kaufpreis (€)'}</span><input className="input" inputMode="decimal" value={principal} onChange={(e) => setPrincipal(e.target.value)} required /></label>}
        {!isLease && <label className="field"><span>Sollzins pro Jahr (%)</span><input className="input" inputMode="decimal" value={rate} onChange={(e) => setRate(e.target.value)} placeholder="5,5" /></label>}
        <label className="field">
          <span>{isLease ? 'Monatliche Rate brutto (€)' : 'Monatliche Rate (€), leer = aus dem Zinssatz rechnen'}</span>
          <input className="input" inputMode="decimal" value={installment} onChange={(e) => setInstallment(e.target.value)} required={isLease} />
        </label>
        {isLease && (
          <label className="field">
            <span>Umsatzsteuer</span>
            <select className="input" value={taxRate} onChange={(e) => setTaxRate(Number(e.target.value) as 0 | 7 | 19)}>
              <option value={19}>19 %</option><option value={7}>7 %</option><option value={0}>0 %</option>
            </select>
          </label>
        )}
        <label className="field"><span>{isLease ? 'Sonderzahlung brutto (€)' : 'Anzahlung (€)'}</span><input className="input" inputMode="decimal" value={down} onChange={(e) => setDown(e.target.value)} /></label>
        <label className="field"><span>{isLease ? 'Restwert (€)' : 'Schlussrate am Ende (€)'}</span><input className="input" inputMode="decimal" value={balloon} onChange={(e) => setBalloon(e.target.value)} /></label>
        <label className="field"><span>Einmalige Gebühren (€)</span><input className="input" inputMode="decimal" value={fees} onChange={(e) => setFees(e.target.value)} /></label>
        {isLease && <label className="field"><span>Freikilometer pro Jahr</span><input className="input" inputMode="numeric" value={mileage} onChange={(e) => setMileage(e.target.value.replace(/[^\d.]/g, ''))} /></label>}
      </div>
      <label className="field">
        <span>Verwendungszweck und Begründung</span>
        <textarea className="input textarea" value={purpose} onChange={(e) => setPurpose(e.target.value)} maxLength={2000} rows={4} placeholder="Wofür wird das Geld gebraucht, und warum lohnt es sich? Dieser Absatz steht in der Mappe." />
      </label>
      <label className="field"><span>Interne Bemerkung (steht nicht in der Mappe)</span><input className="input" value={note} onChange={(e) => setNote(e.target.value)} maxLength={2000} /></label>

      {settled && preview.data && (
        <>
          <p className="fin-preview" role="status">Wirkia rechnet eine Rate von {preview.data.monthlyCents !== null ? formatCents(preview.data.monthlyCents) : '–'} im Monat.</p>
          <AffordabilityCard assessment={preview.data} title="Tragfähigkeit (Vorschau)" />
        </>
      )}
      {settled && preview.isError && <div className="alert alert--error" role="alert">Die Tragfähigkeit ließ sich nicht rechnen.</div>}

      <div className="form-actions fin-form-actions">
        <button type="submit" className="btn btn--primary" disabled={pending}>{pending ? 'Wird gespeichert …' : p ? 'Speichern' : 'Vorhaben anlegen'}</button>
        <button type="button" className="btn btn--secondary" onClick={onCancel}>Abbrechen</button>
      </div>
    </form>
  );
}
